import { useState, useEffect, useCallback, useMemo } from 'react'
import { getInventory, updateCard, deleteCard } from '../../services/inventoryService'
import type { Inventory, InventoryCard, StandardCondition, StorageLocation } from '@/types/inventory'
import { STANDARD_CONDITIONS } from '@/types/inventory'
import { InventoryFilters } from './InventoryFilters'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Alert, AlertDescription } from '../ui/alert'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Package, Trash2, Pencil, ImageIcon, Loader2 } from 'lucide-react'
import { PriceEditor } from './PriceEditor'
import { StorageLocationSelect } from './StorageLocationSelect'
import { getStorageLocations } from '@/services/storageLocationService'

export function InventoryTab() {
  const [inventory, setInventory] = useState<Inventory | null>(null)
  const [storageLocations, setStorageLocations] = useState<StorageLocation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [editingPrice, setEditingPrice] = useState<string | null>(null)
  const [filterFunction, setFilterFunction] = useState<((card: InventoryCard) => boolean) | null>(null)
  const [sortFunction, setSortFunction] = useState<((a: InventoryCard, b: InventoryCard) => number) | null>(null)

  const loadInventory = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await getInventory()
      setInventory(data)

      const storageLocations = await getStorageLocations()
      setStorageLocations(storageLocations)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load inventory and storage locations')
      setInventory(null)
      setStorageLocations([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadInventory()
  }, [loadInventory])

  const handleConditionChange = async (card: InventoryCard, condition: StandardCondition) => {
    try {
      setInventory((prev) => (prev ? { ...prev, cards: prev.cards?.map((c) => (c.id === card.id ? { ...c, condition } : c)) } : null))
      const updatedCard = await updateCard(card.id, { condition })
      if (updatedCard) {
        setInventory((prev) => (prev ? { ...prev, cards: prev.cards?.map((c) => (c.id === updatedCard.id ? updatedCard : c)) } : null))
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update condition')
    }
  }

  const handleStorageLocationChange = async (card: InventoryCard, storageLocationId: string | null) => {
    try {
      const updated = await updateCard(card.id, { storageLocationId })
      if (updated) {
        setInventory((prev) => (prev ? { ...prev, cards: prev.cards?.map((c) => (c.id === updated.id ? updated : c)) } : null))
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update storage location')
    }
  }

  const handlePriceChange = async (card: InventoryCard, priceString: string) => {
    try {
      const trimmed = priceString.trim()
      const price = trimmed ? parseFloat(trimmed) : null
      if (trimmed && (isNaN(price!) || price! < 0)) return

      setInventory((prev) => (prev ? { ...prev, cards: prev.cards?.map((c) => (c.id === card.id ? { ...c, priceWhenAdded: price } : c)) } : null))
      const updated = await updateCard(card.id, { priceWhenAdded: price })
      if (updated) {
        setInventory((prev) => (prev ? { ...prev, cards: prev.cards?.map((c) => (c.id === updated.id ? updated : c)) } : null))
      }
      setEditingPrice(null)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update price')
    }
  }

  const handleDelete = async (card: InventoryCard) => {
    if (!confirm('Remove this card from your inventory?')) return
    setDeletingId(card.id)
    try {
      await deleteCard(card.id)
      loadInventory()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove card')
    } finally {
      setDeletingId(null)
    }
  }

  const formatCurrency = (value: number | null | undefined) => {
    if (value == null) return '—'
    return `$${value.toFixed(2)}`
  }

  const cards = useMemo(() => inventory?.cards ?? [], [inventory])
  const filteredCards = useMemo(() => {
    const defaultFilter = () => true
    const defaultSort = () => 0
    return cards.filter(filterFunction ?? defaultFilter).sort(sortFunction ?? defaultSort)
  }, [cards, filterFunction, sortFunction])

  const handleFilterChange = useCallback((fn: (card: InventoryCard) => boolean) => {
    setFilterFunction(() => fn)
  }, [])

  const handleSortChange = useCallback((fn: (a: InventoryCard, b: InventoryCard) => number) => {
    setSortFunction(() => fn)
  }, [])

  const handleStorageLocationCreated = useCallback(async (storageLocation: StorageLocation) => {
    setStorageLocations((prev) => [...prev, storageLocation])

    const newLocations = await getStorageLocations()
    setStorageLocations(newLocations)
  }, [])

  if (isLoading) {
    return (
      <Card className="min-h-[400px]">
        <CardHeader>
          <CardTitle className="text-3xl">Inventory</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground">Loading inventory...</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className="min-h-[400px]">
        <CardHeader>
          <CardTitle className="text-3xl">Inventory</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center gap-4 py-12">
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <Button onClick={loadInventory}>Retry</Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  const isEmpty = cards.length === 0

  return (
    <Card className="min-h-[400px]">
      <CardHeader>
        <CardTitle className="text-3xl">Inventory</CardTitle>
      </CardHeader>
      <CardContent>
        {isEmpty && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="flex items-center justify-center w-20 h-20 rounded-full bg-muted mb-4">
              <Package className="h-10 w-10 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Your inventory is empty</h3>
            <p className="text-muted-foreground">Add cards from the Scan or Collection tab to build your inventory.</p>
          </div>
        )}

        {!isEmpty && (
          <>
            <InventoryFilters cards={cards} onFilteredChange={handleFilterChange} onSortChange={handleSortChange} />
            <div className="overflow-x-auto -mx-4 md:mx-0">
              {filteredCards.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">No cards match your filters. Try adjusting your search or filter criteria.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-28">Image</TableHead>
                      <TableHead>Card</TableHead>
                      <TableHead>Set</TableHead>
                      <TableHead>Condition</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>CardMarket</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Added</TableHead>
                      <TableHead className="w-12">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCards.map((card) => (
                      <TableRow key={card.id}>
                        <TableCell>
                          <div className="w-12 h-16 md:w-20 md:h-28 rounded-lg overflow-hidden bg-muted border flex items-center justify-center shrink-0">
                            {card.card?.cardData.imageUrl ? (
                              <img
                                src={card.card.cardData.imageUrl}
                                alt={card.card.name ?? card.cardId}
                                className="w-full h-full object-contain"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement
                                  target.style.display = 'none'
                                }}
                              />
                            ) : (
                              <ImageIcon className="h-6 w-6 text-muted-foreground" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{card.card?.name ?? card.cardId}</span>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{card.card?.set?.name ?? '—'}</TableCell>
                        <TableCell>
                          <Select
                            value={card.condition ?? '__none__'}
                            onValueChange={(val) => {
                              if (val === '__none__') return
                              handleConditionChange(card, val as StandardCondition)
                            }}
                          >
                            <SelectTrigger className="h-8 w-[120px]">
                              <SelectValue placeholder="—" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">—</SelectItem>
                              {STANDARD_CONDITIONS.map((c) => (
                                <SelectItem key={c} value={c}>
                                  {c}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          {editingPrice === card.id ? (
                            <PriceEditor initialValue={card.priceWhenAdded} onSave={(price) => handlePriceChange(card, price)} onCancel={() => setEditingPrice(null)} />
                          ) : (
                            <div className="flex items-center gap-2 group cursor-pointer w-fit" onClick={() => setEditingPrice(card.id)}>
                              <span>{formatCurrency(card.priceWhenAdded)}</span>
                              <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {card.card?.cardMarketUrl ? (
                            <Button variant="link" size="sm" className="h-auto p-0" asChild>
                              <a href={card.card.cardMarketUrl} target="_blank" rel="noopener noreferrer">
                                View →
                              </a>
                            </Button>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <StorageLocationSelect
                            value={card.storageLocation?.id ?? null}
                            onChange={(id) => handleStorageLocationChange(card, id)}
                            storageLocations={storageLocations}
                            onStorageLocationCreated={handleStorageLocationCreated}
                          />
                        </TableCell>
                        <TableCell className="text-muted-foreground">{card.addedAt.toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(card)}
                            disabled={deletingId === card.id}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            aria-label="Remove card"
                          >
                            {deletingId === card.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
